import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { MiniSegmentedControl } from './mini-segmented-control';
import { useScrollDirection } from '@hooks/useScrollDirection';
import type { PlayerStatsVenue } from '@api-service/player-stats';
import {
  BASIS_OPTIONS,
  VENUE_OPTIONS,
  WINDOW_OPTIONS,
  toSeasonLabel,
  type FormWindow,
  type ProfileFilters,
  type StatsBasis
} from '@lib/page-utils/player-profile/filters';

type ChipOption<T extends string | number> = {
  value: T;
  label: string;
};

type ChipGroupProps<T extends string | number> = {
  label: string;
  options: ChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Locks the group behind the paywall and routes clicks to the modal. */
  isLocked?: boolean;
  onLockedClick?: () => void;
};

const ChipGroup = <T extends string | number>({
  label,
  options,
  value,
  onChange,
  isLocked = false,
  onLockedClick
}: ChipGroupProps<T>) => (
  <div className="flex shrink-0 flex-col items-start gap-1.5">
    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500">
      {label}
      {isLocked && <span className="ml-1 text-neutral-400">🔒</span>}
    </span>
    <MiniSegmentedControl
      ariaLabel={label}
      options={options}
      value={value}
      onChange={onChange}
      isLocked={isLocked}
      onLockedClick={onLockedClick}
    />
  </div>
);

type PlayerProfileFilterBarProps = {
  filters: ProfileFilters;
  seasons: number[];
  onSeasonChange: (season: number) => void;
  onVenueChange: (venue: PlayerStatsVenue) => void;
  onWindowChange: (window: FormWindow) => void;
  onBasisChange: (basis: StatsBasis) => void;
  /** Venue, window and basis are the tool; they sit behind the paywall. */
  isRestricted: boolean;
  onRestrictedClick: () => void;
  /** Distance from the viewport top, clearing the fixed header when there is
      one. In the app view there is no header, so the bar pins to the very top. */
  stickyOffsetPx?: number;
};

export const PlayerProfileFilterBar = ({
  filters,
  seasons,
  onSeasonChange,
  onVenueChange,
  onWindowChange,
  onBasisChange,
  isRestricted,
  onRestrictedClick,
  stickyOffsetPx = 0
}: PlayerProfileFilterBarProps) => {
  // The bar stays pinned while the user reads down the page, when narrowing
  // the numbers on screen is what filters are for, and gets out of the way on
  // the scroll back up towards the hero.
  const scrollDirection = useScrollDirection();

  // Stuckness is observed off a sentinel rather than the bar itself: the
  // hide transform moves the bar's own bounding box, which would feed back
  // into the measurement. An IntersectionObserver only fires when the
  // sentinel crosses the offset line, rather than measuring layout on every
  // scroll event.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Above the shrunken root's top edge means scrolled past the bar's
        // natural position; merely leaving below the viewport must not count.
        setIsStuck(
          entry.boundingClientRect.top <
            (entry.rootBounds?.top ?? stickyOffsetPx)
        );
      },
      { rootMargin: `-${stickyOffsetPx}px 0px 0px 0px` }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [stickyOffsetPx]);

  // Hiding only applies while stuck: in normal flow a translated bar would
  // leave a hole in the page below the hero.
  const isHidden = isStuck && scrollDirection === 'up';

  return (
    <>
      <div ref={sentinelRef} aria-hidden />
      <div
        style={{ top: stickyOffsetPx }}
        className={classNames(
          'sticky z-30 border-b border-neutral-200 bg-white shadow-100 transition-transform duration-200',
          isHidden && '-translate-y-[150%]'
        )}
      >
        <div className="mx-auto flex max-w-7xl items-start gap-x-5 overflow-x-auto px-4 py-3 sm:flex-wrap sm:gap-x-7 sm:gap-y-4 sm:px-6 lg:px-12">
          <ChipGroup
            label="Season"
            options={seasons.map((season) => ({
              value: season,
              label: toSeasonLabel(season)
            }))}
            value={filters.season}
            onChange={onSeasonChange}
          />
          <ChipGroup
            label="Venue"
            options={VENUE_OPTIONS}
            value={filters.venue}
            onChange={onVenueChange}
            isLocked={isRestricted}
            onLockedClick={onRestrictedClick}
          />
          <ChipGroup
            label="Window"
            options={WINDOW_OPTIONS}
            value={filters.window}
            onChange={onWindowChange}
            isLocked={isRestricted}
            onLockedClick={onRestrictedClick}
          />
          <div className="lg:ml-auto">
            <ChipGroup
              label="Basis"
              options={BASIS_OPTIONS}
              value={filters.basis}
              onChange={onBasisChange}
              isLocked={isRestricted}
              onLockedClick={onRestrictedClick}
            />
          </div>
        </div>
      </div>
    </>
  );
};
